import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { anthropometricService } from '../services';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Plus, TrendingUp, TrendingDown, Activity, Scale, Percent, Calendar } from 'lucide-react';

const Anthropometric = () => {
  const [stats, setStats] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('weight');
  const [evolutionData, setEvolutionData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedMetric) {
      loadEvolutionData(selectedMetric);
    }
  }, [selectedMetric]);

  const loadData = async () => {
    try {
      const [statsRes, measurementsRes] = await Promise.all([
        anthropometricService.getStats(),
        anthropometricService.getAll(),
      ]);
      
      setStats(statsRes.data);
      setMeasurements(measurementsRes.data);
    } catch (error) {
      console.error('Error loading anthropometric data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvolutionData = async (metric) => {
    try {
      const response = await anthropometricService.getEvolution(metric, 12);
      const formattedData = response.data.data.map(item => ({
        date: new Date(item.measurement_date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
        value: parseFloat(item[metric]) || 0,
      }));
      setEvolutionData(formattedData);
    } catch (error) {
      console.error('Error loading evolution data:', error);
    }
  };

  const getMetricInfo = (metric) => {
    const metrics = {
      weight: { label: 'Peso', unit: 'kg', color: '#8b5cf6' },
      bmi: { label: 'IMC', unit: '', color: '#3b82f6' },
      body_fat_percentage: { label: '% Grasa', unit: '%', color: '#f59e0b' },
      lean_mass: { label: 'Masa Magra', unit: 'kg', color: '#10b981' },
      perimeter_waist_min: { label: 'Cintura', unit: 'cm', color: '#ef4444' },
      perimeter_arm_flexed: { label: 'Brazo', unit: 'cm', color: '#6366f1' },
    };
    return metrics[metric] || { label: metric, unit: '', color: '#6b7280' };
  };

  const renderChangeIndicator = (change) => {
    if (!change || change.absolute === 0) return null;

    const isPositive = change.absolute > 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';

    return (
      <div className={`flex items-center gap-1 ${colorClass} text-sm font-bold`}>
        <Icon size={16} />
        <span>{Math.abs(change.absolute).toFixed(2)} ({Math.abs(change.percentage).toFixed(1)}%)</span>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <div className="inline-block w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xl text-gray-600 mt-4 font-semibold">Cargando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-5xl font-extrabold mb-2 text-gray-900">
              📏 Antropometría
            </h1>
            <p className="text-gray-700 text-lg font-semibold">
              Seguimiento profesional de tu composición corporal
            </p>
          </div>
          <button
            onClick={() => navigate('/anthropometric/new')}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
            }}
            className="text-white font-bold py-4 px-8 rounded-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center space-x-2"
          >
            <Plus size={24} />
            <span>Nueva Medición</span>
          </button>
        </div>

        {/* Stats Cards */}
        {stats && stats.latest_measurement ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Peso */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border-2 border-purple-200 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <Scale className="text-purple-600" size={32} />
                  <span className="text-xs font-bold text-purple-600 bg-purple-200 px-3 py-1 rounded-full">
                    PESO
                  </span>
                </div>
                <p className="text-4xl font-extrabold text-purple-900 mb-1">
                  {stats.latest_measurement.weight?.toFixed(1) || '--'} <span className="text-2xl">kg</span>
                </p>
                {stats.evolution?.weight_change && renderChangeIndicator(stats.evolution.weight_change)}
              </div>

              {/* IMC */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border-2 border-blue-200 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <Activity className="text-blue-600" size={32} />
                  <span className="text-xs font-bold text-blue-600 bg-blue-200 px-3 py-1 rounded-full">
                    IMC
                  </span>
                </div>
                <p className="text-4xl font-extrabold text-blue-900 mb-1">
                  {stats.latest_measurement.bmi?.toFixed(1) || '--'}
                </p>
                {stats.evolution?.bmi_change && renderChangeIndicator(stats.evolution.bmi_change)}
              </div>

              {/* % Grasa */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border-2 border-orange-200 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <Percent className="text-orange-600" size={32} />
                  <span className="text-xs font-bold text-orange-600 bg-orange-200 px-3 py-1 rounded-full">
                    % GRASA
                  </span>
                </div>
                <p className="text-4xl font-extrabold text-orange-900 mb-1">
                  {stats.latest_measurement.body_fat_percentage?.toFixed(1) || '--'} <span className="text-2xl">%</span>
                </p>
                {stats.evolution?.body_fat_change && renderChangeIndicator(stats.evolution.body_fat_change)}
              </div>

              {/* Masa Magra */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-2 border-green-200 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <TrendingUp className="text-green-600" size={32} />
                  <span className="text-xs font-bold text-green-600 bg-green-200 px-3 py-1 rounded-full">
                    MASA MAGRA
                  </span>
                </div>
                <p className="text-4xl font-extrabold text-green-900 mb-1">
                  {stats.latest_measurement.lean_mass?.toFixed(1) || '--'} <span className="text-2xl">kg</span>
                </p>
                {stats.evolution?.lean_mass_change && renderChangeIndicator(stats.evolution.lean_mass_change)}
              </div>
            </div>

            {/* Chart Section */}
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center">
                  <span className="text-4xl mr-3">📊</span>
                  Evolución Temporal
                </h2>
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  className="px-4 py-2 bg-gray-100 border-2 border-gray-300 rounded-xl font-bold text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="weight">Peso (kg)</option>
                  <option value="bmi">IMC</option>
                  <option value="body_fat_percentage">% Grasa Corporal</option>
                  <option value="lean_mass">Masa Magra (kg)</option>
                  <option value="perimeter_waist_min">Cintura (cm)</option>
                  <option value="perimeter_arm_flexed">Brazo Contraído (cm)</option>
                </select>
              </div>

              {evolutionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={evolutionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6b7280"
                      style={{ fontSize: '12px', fontWeight: 'bold' }}
                    />
                    <YAxis 
                      stroke="#6b7280"
                      style={{ fontSize: '12px', fontWeight: 'bold' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '2px solid #8b5cf6',
                        borderRadius: '12px',
                        fontWeight: 'bold'
                      }}
                      formatter={(value) => [
                        `${value.toFixed(2)} ${getMetricInfo(selectedMetric).unit}`,
                        getMetricInfo(selectedMetric).label
                      ]}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke={getMetricInfo(selectedMetric).color}
                      strokeWidth={3}
                      dot={{ fill: getMetricInfo(selectedMetric).color, r: 5 }}
                      activeDot={{ r: 8 }}
                      name={getMetricInfo(selectedMetric).label}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No hay suficientes datos para mostrar el gráfico</p>
                </div>
              )}
            </div>

            {/* Recommendations */}
            {stats.recommendations && stats.recommendations.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="text-4xl mr-3">💡</span>
                  Recomendaciones Personalizadas
                </h2>
                <div className="space-y-4">
                  {stats.recommendations.map((rec, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-xl border-2 ${
                        rec.type === 'success' ? 'bg-green-50 border-green-300' :
                        rec.type === 'warning' ? 'bg-yellow-50 border-yellow-300' :
                        'bg-blue-50 border-blue-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">
                          {rec.type === 'success' ? '✅' : rec.type === 'warning' ? '⚠️' : 'ℹ️'}
                        </span>
                        <div>
                          <p className="font-bold text-gray-900 mb-1">{rec.category}</p>
                          <p className="text-gray-700">{rec.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Measurements */}
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Calendar size={32} className="mr-3 text-purple-600" />
                Historial de Mediciones
              </h2>
              {measurements.length > 0 ? (
                <div className="space-y-4">
                  {measurements.slice(0, 5).map((measurement) => (
                    <div
                      key={measurement.id}
                      onClick={() => navigate(`/anthropometric/${measurement.id}`)}
                      className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                          <p className="font-bold text-purple-900 text-lg">
                            {new Date(measurement.measurement_date).toLocaleDateString('es-ES', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                          {measurement.notes && (
                            <p className="text-sm text-gray-600 mt-1">{measurement.notes}</p>
                          )}
                        </div>
                        <div className="flex gap-4 flex-wrap">
                          {measurement.weight && (
                            <div className="text-center">
                              <p className="text-2xl font-bold text-purple-600">{measurement.weight} kg</p>
                              <p className="text-xs text-gray-500 font-medium">Peso</p>
                            </div>
                          )}
                          {measurement.bmi && (
                            <div className="text-center">
                              <p className="text-2xl font-bold text-blue-600">{measurement.bmi}</p>
                              <p className="text-xs text-gray-500 font-medium">IMC</p>
                            </div>
                          )}
                          {measurement.body_fat_percentage && (
                            <div className="text-center">
                              <p className="text-2xl font-bold text-orange-600">{measurement.body_fat_percentage}%</p>
                              <p className="text-xs text-gray-500 font-medium">Grasa</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600 text-lg mb-2">📝 Aún no tienes mediciones registradas</p>
                  <p className="text-gray-500 text-sm">Comienza tu seguimiento antropométrico hoy</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-12 text-center border-2 border-purple-200">
            <div className="text-6xl mb-4">📏</div>
            <h2 className="text-3xl font-bold mb-3 text-gray-800">
              Comienza tu Seguimiento Antropométrico
            </h2>
            <p className="text-gray-600 mb-6 text-lg max-w-2xl mx-auto">
              Registra tus mediciones corporales para obtener análisis profesionales, cálculos automáticos de IMC, 
              porcentaje de grasa corporal y recomendaciones personalizadas.
            </p>
            <button
              onClick={() => navigate('/anthropometric/new')}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
              }}
              className="text-white font-bold py-4 px-10 rounded-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center space-x-3 mx-auto"
            >
              <Plus size={28} />
              <span>Crear Primera Medición</span>
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Anthropometric;
